/* Native Shop navigation coordinator. Plays the selected Devour transition, then completes the
 * original anchor destination. It owns no Shopify loading, history or rendering: Embedded Shop
 * (which preventDefaults a click it accepts) and the Home tile handoff keep those paths. */
(function () {
  'use strict';
  const storefront = window.BSStorefront;
  if (!storefront?.lifecycle || storefront.devourNavigation) return;
  const selector = 'a[data-bs-home-handoff="shop"][href]';
  // Hard ceilings: animation may delay navigation by budgetMs; a stalled navigation releases the cover after holdMs.
  const budgetMs = 1500, holdMs = 10000;

  storefront.devourNavigation = {
    budgetMs, holdMs,
    unregister: storefront.lifecycle.register('bs-devour-navigation', '.bs-home-hub', root => {
      if (!storefront.devourGate?.enabled() || typeof storefront.shopTransitions?.create !== 'function') return;
      const removers = [];
      let disposed = false, pending = null, returning = null;

      function listen(target, type, handler, options) {
        const guarded = event => { if (!disposed) handler(event); };
        target.addEventListener(type, guarded, options);
        removers.push(() => target.removeEventListener(type, guarded, options));
      }

      // Presentation release only; it never navigates.
      function release() {
        const current = pending;
        pending = null; // Invalidate continuations before the motion resolves.
        if (!current) return;
        clearTimeout(current.timer);
        try { current.motion.dispose(); } catch (ignored) { /* Host removal remains mandatory. */ }
        current.host.remove();
      }

      function complete(current) {
        if (disposed || pending !== current || current.leaving) return;
        clearTimeout(current.timer);
        if (!root.isConnected || !current.link.isConnected) { release(); return; }
        current.leaving = true;
        // The cover stays until unload so Home cannot flash back while Shopify responds.
        current.timer = setTimeout(() => { if (pending === current) release(); }, holdMs);
        try { location.assign(current.href); } catch (error) { release(); }
      }

      function onClick(event) {
        const anchor = event.target?.closest?.('a[href]');
        const link = anchor && anchor.matches(selector) && anchor.closest('.bs-home-hub') === root ? anchor : null;
        if (!link) return;
        // A prevented event is already owned (Embedded Shop or another handler); never intercept twice.
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (link.hasAttribute('download') || (link.target && link.target.toLowerCase() !== '_self')) return;
        if (pending) { event.preventDefault(); return; }
        // Everything before preventDefault may fail back to the ordinary anchor.
        let host = null, motion = null;
        try {
          const url = new URL(link.href, location.href);
          if (!/^https?:$/.test(url.protocol) || url.origin !== location.origin || url.href === location.href) return;
          if (url.pathname === location.pathname && url.search === location.search && url.hash) return;
          if (document.hidden || !root.isConnected || typeof window.matchMedia !== 'function' ||
              window.matchMedia('(prefers-reduced-motion: reduce)').matches || !storefront.skullActor?.get(root)?.available(link)) return;
          host = document.createElement('div');
          host.className = 'bs-devour-transition'; host.setAttribute('aria-hidden', 'true'); host.inert = true;
          document.body.appendChild(host);
          if (getComputedStyle(host).position !== 'fixed') { host.remove(); return; }
          motion = storefront.shopTransitions.create({ root, link, surface: host });
          if (!motion.active) { motion.dispose(); host.remove(); return; }
          const current = { link, href: link.href, host, motion, timer: 0, leaving: false };
          pending = current; returning = link;
          current.timer = setTimeout(() => complete(current), budgetMs);
          const done = () => complete(current);
          motion.finished.then(done, done);
          link.focus({ preventScroll: true });
          event.preventDefault(); // Existing delegated handoff/audio honor this.
        } catch (error) {
          release();
          try { motion?.dispose(); } catch (ignored) { /* Native navigation continues. */ }
          host?.remove();
        }
      }

      function dispose() {
        disposed = true;
        release();
        returning = null;
        removers.splice(0).forEach(remove => remove());
      }

      try {
        // Capture on the Home root runs before the unchanged bubble-phase handoff and audio delegates.
        listen(root, 'click', onClick, true);
        listen(document, 'click', event => {
          const anchor = event.target?.closest?.('a[href]');
          if (anchor && pending && anchor !== pending.link) release();
        });
        listen(document, 'keydown', event => { if (event.key === 'Escape') release(); });
        listen(window, 'resize', () => pending?.motion.resize());
        listen(document, 'visibilitychange', () => { if (document.hidden && pending) complete(pending); });
        listen(window, 'pagehide', release);
        listen(window, 'pageshow', event => {
          release();
          if (event.persisted && returning && root.isConnected && returning.isConnected && root.contains(returning)) returning.focus({ preventScroll: true });
          returning = null;
        });
        return dispose;
      } catch (error) { dispose(); throw error; }
    })
  };
})();
